import { Component, OnDestroy, OnInit } from '@angular/core';
import { WorkflowDto } from '../../../service/dto';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpWorkflowService } from '../../../service/http-workflow.service';
import { of, Subscription, switchMap } from 'rxjs';
import {
	FormArray,
	FormBuilder,
	FormControl,
	FormGroup,
	Validators,
} from '@angular/forms';
import {
	doGetInvalidControlsList,
	doTouchAllFields,
	WrapperWorkflowDto,
	WrapperWorkflowStepDto,
} from './workflow-utils';
import { DraftService } from '../../../service/draft.service';
import { NzMessageService } from 'ng-zorro-antd/message';

@Component({
	selector: 'app-workflow-form',
	standalone: false,
	templateUrl: './workflow-form.component.html',
	styleUrl: './workflow-form.component.scss',
})
export class WorkflowFormComponent implements OnInit, OnDestroy {
	static idCounter = Date.now() * -1;
	subscription = new Subscription();
	workflow?: WrapperWorkflowDto;
	selectedStepIndex = 0;
	mode!: 'Workflow' | 'ProductWorkflow';
	productId?: number;
	categoryPlaceholders?: { [key: string]: string };

	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private httpService: HttpWorkflowService,
		private fb: FormBuilder,
		private draftService: DraftService,
		private messageService: NzMessageService,
	) {}
	public static getNextId(): number {
		return --this.idCounter;
	}

	ngOnInit(): void {
		this.subscription.add(
			this.activatedRoute.paramMap.subscribe((paramMap) => {
				console.log(
					`POST1: Reloading workflow.  ProductId:[${paramMap.get('productId')}]  ProductType:[${paramMap.get('productType')}]  Mode:[${this.activatedRoute.snapshot.data['mode']}]`,
				);
				this.workflow = undefined;
				this.productId = undefined;

				this.mode =
					this.activatedRoute.snapshot.data['mode'] ?? 'Workflow';
				if (this.mode == 'Workflow') {
					const id = paramMap.get('id');
					this.productId = undefined;
					return this.draftService
						.prompt<WorkflowDto>(
							'workflow',
							id == undefined ? undefined : +id,
						)
						.pipe(
							switchMap((item) => {
								if (item == false) {
									if (id) {
										return this.httpService.get(+id);
									} else {
										return this.httpService.newWorkflow();
									}
								}
								return of(item);
							}),
						)
						.subscribe((result) => {
							this.workflow = this.init(result);
							this.loadCategoryPlaceholders();
						});
				} else {
					this.productId = +paramMap.get('productId')!;
					const productType = paramMap.get('productType')!;
					console.log(
						`POST2: Reloading workflow.  ProductId:[${paramMap.get('productId')}]  ProductType:[${paramMap.get('productType')}]  Mode:[${this.activatedRoute.snapshot.data['mode']}]`,
					);

					return this.httpService
						.getForProduct(productType!, this.productId)
						.subscribe((result) => {
							this.mode = 'ProductWorkflow';
							this.workflow = this.init(result);
							this.loadCategoryPlaceholders();
						});
				}
			}),
		);
	}
	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	init(input: WorkflowDto): WrapperWorkflowDto | undefined {
		const out = new WrapperWorkflowDto();
		const steps = input.steps
			.filter((item) => item.questionnaire == false)
			.map((item) =>
				WrapperWorkflowStepDto.create(
					this.fb,
					out,
					item,
					item.readOnlyFields,
				),
			)
			.sort((i1, i2) => {
				if (i1._stepOrder && i2._stepOrder) {
					return i1._stepOrder - i2._stepOrder;
				}
				if (i1._stepOrder) {
					return -1;
				}
				return 1;
			});

		const form = this.fb.group({
			id: [input.id],
			name: [input.name, [Validators.required]], //Add unique check
			label: [input.label, [Validators.required]],
			description: [input.description, [Validators.required]],
			autoAssign: [input.autoAssign],
			linkMemberships: [input.linkMemberships],
			categoryTemplate: [input.categoryTemplate],
			ticketMode: [input.ticketMode, Validators.required],
			linkToOtherTicketEnabled: [input.linkToOtherTicketEnabled],
			referencePrefix: [input.referencePrefix],
			restrictToPolicies: [input.restrictToPolicies],
			steps: this.fb.array(steps.map((item) => item.formGroup)), //, Validators.required),
			questionnaire: this.fb.group({}) as FormGroup,

			slaDurationBreach: [
				input.slaDurationBreach,
				[
					Validators.pattern(
						/^(\d+\s*(y|year|years|M|month|months|d|day|days|W|week|weeks|h|hour|hours|m|minute|minutes|s|second|seconds|ww|work\s*week|work\s*weeks|wd|work\s*day|work\s*days|wh|work\s*hour|work\s*hours)\s*)+$/i,
					),
				],
			],
			slaDurationEscalation: [
				input.slaDurationEscalation,
				[
					Validators.pattern(
						/^(\d+\s*(y|year|years|M|month|months|d|day|days|W|week|weeks|h|hour|hours|m|minute|minutes|s|second|seconds|ww|work\s*week|work\s*weeks|wd|work\s*day|work\s*days|wh|work\s*hour|work\s*hours)\s*)+$/i,
					),
				],
			],
			slaDurationWarning: [
				input.slaDurationWarning,
				[
					Validators.pattern(
						/^(\d+\s*(y|year|years|M|month|months|d|day|days|W|week|weeks|h|hour|hours|m|minute|minutes|s|second|seconds|ww|work\s*week|work\s*weeks|wd|work\s*day|work\s*days|wh|work\s*hour|work\s*hours)\s*)+$/i,
					),
				],
			],
		});
		const questionnaire = input.steps
			.filter((item) => item.questionnaire == true)
			.map((item) =>
				WrapperWorkflowStepDto.create(
					this.fb,
					out,
					item,
					item.readOnlyFields,
				),
			)[0];
		if (questionnaire) {
			form.addControl('questionnaire', questionnaire.formGroup);
		}

		out.payload = input;
		out.form = form;
		out.steps = steps;
		out.questionnaire = questionnaire;
		// if (steps.length == 0) {
		// 	WrapperWorkflowStepDto.add(this.fb, out);
		// }
		return out;
	}
	addStep() {
		WrapperWorkflowStepDto.add(this.fb, this.workflow!);
		this.selectedStepIndex = this.workflow!.steps.length - 1;
	}
	removeStep({ index }: { index: number }) {
		WrapperWorkflowStepDto.remove(this.workflow!, index);
	}

	doSave() {
		const payload = this.workflow!.postBody;
		console.log('Payload', payload);
		if (this.workflow!.payload.id > 0) {
			const observable =
				this.mode == 'Workflow'
					? this.httpService.edit(this.workflow!.payload.id, payload)
					: this.httpService.editForProduct(
							this.workflow!.payload.id,
							this.productId!,
							payload,
						);

			observable.subscribe(
				(result) => {
					this.draftService.removeDraft(
						'workflow',
						this.workflow!.payload.id,
					);
					if (this.mode == 'Workflow') {
						this.router.navigate(['/workflow/list']);
					} else {
						this.messageService.info('Saved...');
					}
				},
				(error) => {
					this.messageService.error('Failed to create workflow');
				},
			);
		} else {
			this.httpService.save(payload).subscribe(
				(result) => {
					this.draftService.removeDraft(
						'workflow',
						this.workflow!.payload.id,
					);
					this.router.navigate(['/workflow/list']);
				},
				(error) => {
					this.messageService.error('Failed to update workflow');
				},
			);
		}
	}
	doCancel() {
		this.router.navigate(['/workflow/list']);
	}
	getActiveStep(): WrapperWorkflowStepDto {
		if (this.selectedStepIndex < 0) {
			this.selectedStepIndex = 0;
		}
		if (this.selectedStepIndex >= this.workflow!.steps.length) {
			this.selectedStepIndex = this.workflow!.steps.length - 1;
		}
		return this.workflow!.steps[this.selectedStepIndex];
	}
	touchAllFields() {
		doTouchAllFields(this.workflow!.form);
	}
	saveAsDraft() {
		this.draftService.saveAsDraft(
			'workflow',
			this.workflow!.payload.id > 0 ? this.workflow!.payload.id : 0,
			this.workflow?.postBody,
		);
	}
	getInvalidControlsList() {
		return doGetInvalidControlsList(this.workflow!.form);
	}

	get isProductWorkflow(): boolean {
		return this.mode == 'ProductWorkflow';
	}
	enableQuestionnaire() {
		WrapperWorkflowStepDto.addQuestionnaire(this.fb, this.workflow!);
	}
	disableQuestionnaire() {
		WrapperWorkflowStepDto.removeQuestionnaire(this.workflow!);
	}

	loadCategoryPlaceholders() {
		if (!this.workflow) {
			return;
		}

		const payload = this.workflow.postBody;

		const observable =
			this.workflow.payload.id > 0
				? this.httpService.getEmailPlaceholders(
						this.workflow.payload.id,
						payload,
					)
				: this.httpService.getEmailPlaceholders(payload);

		this.subscription.add(
			observable.subscribe((result: { [key: string]: string }) => {
				this.categoryPlaceholders = result;
			}),
		);
	}
}
